import React from 'react';
import ReadMore from 'components/ReadMore';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Spring } from 'react-spring/renderprops';

import PanelContainer from 'components/PanelContainer';
import CompanyLogo from 'components/CompanyLogo';
import RatingStarsWidgetEditable from 'components/RatingStarsWidgetEditable';
import RatingStarsWidget from 'components/RatingStarsWidget';
import LinkBuilder from 'helpers/link-builder';
import { REVIEW_TYPES } from 'constants/review-types';
import { ANIMATION_TIMEOUT_INOUT } from 'constants/animation.js';

import './styles.css';
import NumberFormat from 'react-number-format';

const ReviewItem = props => {
  const {
    item,
    actions: {
      handleReviewRatingUpdate,
      handleClickReviewLink,
      handleBeforeReviewRatingUpdate,
      handleClickSendMessage,
    },
  } = props;

  const {
    ReviewType,
    ReviewType: baseType,
    TitleName,
    DateAdd,
    TypeName,
    Text,
    Rating,
    FirmName,
    FirmLogo = null,
    UrlAlias,
    ResponseCount,
    PublicName,
    Position,
    HasCandidateRating,
    AllowView,
    ReviewSource,
    ReviewOwner,
    IsCandidateApproved,
  } = item;

  const disableSendMessage =
    ReviewSource !== 'Internal' || IsCandidateApproved === 'No' || ReviewOwner;

  const reviewType =
    baseType === REVIEW_TYPES.OVERVIEW ? REVIEW_TYPES.COMPANY : baseType;
  const responseText = Number(ResponseCount) ? (
    <>
      <NumberFormat
        thousandSeparator
        value={ResponseCount}
        displayType={'text'}
      />{' '}
      {Number(ResponseCount) === 1 ? 'response' : 'responses'}
    </>
  ) : (
    'No responses'
  );

  const firmLink = LinkBuilder.getFirmLink(UrlAlias);

  let link = null;
  if (AllowView === 'No') {
    link = '#';
  } else if (ReviewType === REVIEW_TYPES.EMPLOYEE) {
    link = LinkBuilder.getEmployeeLink(UrlAlias, PublicName);
  } else if (ReviewType === REVIEW_TYPES.OVERVIEW) {
    link = LinkBuilder.getOverviewLink(UrlAlias, PublicName);
  } else if (ReviewType === REVIEW_TYPES.INTERVIEW) {
    link = LinkBuilder.getInterviewLink(UrlAlias, PublicName);
  }

  const handleLinkClick = e => handleClickReviewLink(e, item);

  const handleRatingUpdate = value => {
    handleReviewRatingUpdate(item, value);
  };

  const handleBeforeRatingUpdate = () => {
    return handleBeforeReviewRatingUpdate(item);
  };

  const handleSendMessage = () => {
    if (!disableSendMessage) {
      handleClickSendMessage(item);
    }
  };

  return (
    <Spring
      from={{ opacity: 0 }}
      to={{ opacity: 1 }}
      config={{ duration: ANIMATION_TIMEOUT_INOUT }}
    >
      {props => (
        <div style={props}>
          <PanelContainer styles={{ padding: 0 }}>
            <article className="reviews-item">
              <div className="reviews-item__container">
                <div className="reviews-item__logo">
                  <CompanyLogo
                    name={FirmName}
                    logo={FirmLogo}
                    link={firmLink}
                    styles={{
                      width: '110px',
                      height: '110px',
                      fontSize: '0.9286rem',
                    }}
                  />
                </div>

                <div className="reviews-item__content">
                  <div className="reviews-item__header">
                    <h4 className="reviews-item__title">
                      <NavLink
                        to={link}
                        className="reviews-item__title-link"
                        onClick={handleLinkClick}
                      >
                        {Position && reviewType === 'employee'
                          ? Position
                          : TitleName}
                      </NavLink>
                    </h4>
                    <span className="reviews-item__date">{DateAdd}</span>
                  </div>

                  <div className="reviews-item__main-info">
                    <NavLink to={firmLink} className="reviews-item__firm-link">
                      {FirmName}
                    </NavLink>
                    <span className="reviews-item__type">{TypeName}</span>
                  </div>

                  <div className="reviews-item__text">
                    <ReadMore text={Text} readMoreCharacterLimit={140} />
                  </div>
                </div>
              </div>

              <div className="reviews-item__footer">
                <div className="reviews-item__view-tab">
                  <div className="reviews-item__tab-content">
                    <NavLink
                      to={link}
                      className="reviews-item__review-link"
                      onClick={handleLinkClick}
                    >
                      <span
                        className="reviews-item__tab-icon reviews-item__tab-icon--interview"
                        aria-label="View interview review"
                        role="img"
                      />{' '}
                      <span className="reviews-item__tab-text">
                        View {reviewType} review
                      </span>
                    </NavLink>
                  </div>
                </div>

                <div className="reviews-item__rating-tab">
                  <div className="reviews-item__tab-content">
                    {HasCandidateRating ? (
                      <RatingStarsWidget
                        value={Rating}
                        size={15}
                        showNumber={false}
                      />
                    ) : (
                      <RatingStarsWidgetEditable
                        value={0}
                        size={15}
                        showNumber={false}
                        onChangeHandler={handleRatingUpdate}
                        onBeforeChangeHandler={handleBeforeRatingUpdate}
                      />
                    )}
                    <span className="reviews-item__rating-comment reviews-item__rating-comment--response">
                      {HasCandidateRating ? responseText : 'Rate this review'}
                    </span>
                  </div>
                </div>

                <div className="reviews-item__message-tab">
                  <div className="reviews-item__tab-content">
                    <span
                      className={`reviews-item__message-link${
                        disableSendMessage
                          ? ` reviews-item__message-link_disabled`
                          : ``
                      }`}
                      onClick={handleSendMessage}
                    >
                      <span
                        className={`reviews-item__tab-icon${
                          disableSendMessage
                            ? ` reviews-item__tab-icon_disabled`
                            : ``
                        } reviews-item__tab-icon--message`}
                        aria-label="Send a message"
                        role="img"
                      />{' '}
                      <span className="reviews-item__tab-text">
                        Send a message{' '}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </article>
          </PanelContainer>
        </div>
      )}
    </Spring>
  );
};

ReviewItem.propTypes = {
  item: PropTypes.shape({
    HasCandidateRating: PropTypes.bool.isRequired,
    ReviewOwner: PropTypes.bool.isRequired,
    ReviewID: PropTypes.string.isRequired,
    ReviewType: PropTypes.string.isRequired,
    TitleName: PropTypes.string.isRequired,
    DateAdd: PropTypes.string.isRequired,
    TypeName: PropTypes.string.isRequired,
    Text: PropTypes.string.isRequired,
    Rating: PropTypes.string.isRequired,
    FirmName: PropTypes.string.isRequired,
    UrlAlias: PropTypes.string.isRequired,
    ResponseCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    PublicName: PropTypes.string.isRequired,
    Position: PropTypes.string,
  }).isRequired,
};

export default ReviewItem;
